/**
 * Image Lightbox Component
 *
 * Full-screen image viewer with navigation and keyboard support
 *
 * @module components/common/ImageLightbox
 */

import React, { useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * ImageLightbox Component
 *
 * @param {Object} props
 * @param {Array} props.images - Array of image objects with url, caption, metadata
 * @param {number} props.currentIndex - Current image index
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onNext - Next image handler
 * @param {Function} props.onPrev - Previous image handler
 */
const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const currentImage = images[currentIndex];
  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === images.length - 1;

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case 'ArrowLeft':
          if (!isFirstImage) onPrev();
          break;
        case 'ArrowRight':
          if (!isLastImage) onNext();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    },
    [isFirstImage, isLastImage, onPrev, onNext, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Touch/Swipe support (basic)
  const handleTouchStart = (e) => {
    const touchStartX = e.touches[0].clientX;

    const handleTouchEnd = (endEvent) => {
      const touchEndX = endEvent.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > 50) { // Minimum swipe distance
        if (diff > 0 && !isLastImage) {
          onNext();
        } else if (diff < 0 && !isFirstImage) {
          onPrev();
        }
      }

      window.removeEventListener('touchend', handleTouchEnd);
    };

    window.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      fullscreen
      className="lightbox-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="bg-dark text-white border-0">
        <Modal.Title>
          {currentImage?.caption || `Imagen ${currentIndex + 1} de ${images.length}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark d-flex flex-column p-0">
        {/* Main Image */}
        <div
          className="flex-grow-1 d-flex align-items-center justify-content-center position-relative"
          onTouchStart={handleTouchStart}
        >
          <img
            src={currentImage?.url || currentImage?.photo_url}
            alt={currentImage?.caption || 'Image'}
            className="img-fluid"
            style={{
              maxHeight: '85vh',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />

          {/* Navigation Buttons */}
          {!isFirstImage && (
            <button
              className="btn btn-light position-absolute start-0 ms-3 rounded-circle"
              onClick={onPrev}
              style={{ width: '50px', height: '50px', opacity: 0.8 }}
            >
              <i className="bi bi-chevron-left fs-4"></i>
            </button>
          )}

          {!isLastImage && (
            <button
              className="btn btn-light position-absolute end-0 me-3 rounded-circle"
              onClick={onNext}
              style={{ width: '50px', height: '50px', opacity: 0.8 }}
            >
              <i className="bi bi-chevron-right fs-4"></i>
            </button>
          )}
        </div>

        {/* Image Info Footer */}
        <div className="bg-dark text-white p-3 border-top border-secondary">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {currentImage?.caption && (
                <p className="mb-1">{currentImage.caption}</p>
              )}
              {currentImage?.uploaded_at && (
                <small className="text-muted">
                  <i className="bi bi-calendar me-1"></i>
                  {new Date(currentImage.uploaded_at).toLocaleDateString('es-CR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </small>
              )}
              {currentImage?.uploaded_by_profile?.full_name && (
                <small className="text-muted ms-3">
                  <i className="bi bi-person me-1"></i>
                  {currentImage.uploaded_by_profile.full_name}
                </small>
              )}
              {currentImage?.gps_latitude && currentImage?.gps_longitude && (
                <small className="text-muted ms-3">
                  <i className="bi bi-geo-alt me-1"></i>
                  GPS: {currentImage.gps_latitude.toFixed(6)}, {currentImage.gps_longitude.toFixed(6)}
                </small>
              )}
            </div>
            <div className="text-muted">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

ImageLightbox.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      photo_url: PropTypes.string,
      caption: PropTypes.string,
      uploaded_at: PropTypes.string,
      uploaded_by_profile: PropTypes.shape({
        full_name: PropTypes.string,
      }),
      gps_latitude: PropTypes.number,
      gps_longitude: PropTypes.number,
    })
  ).isRequired,
  currentIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
};

export default ImageLightbox;
